/**
 * API pública de acceso LMS (única puerta de import).
 *
 * React:
 *   import { useAccess } from 'contexts/AccessContext'
 *   const { isAdmin, can, isScopedUser, isEndUser, isSuperadmin } = useAccess()
 *   isAdmin()
 *   can('manageCourseRequests')
 *
 * Sagas / no-React (mismos métodos):
 *   import { getAccess } from 'contexts/AccessContext'
 *   getAccess().isAdmin()
 *   getAccess().can('manageCourseRequests')
 */
import { createContext, useContext } from 'react'
import { LmsAccess } from './lmsAccess'
import { useAccessControl } from './useAccessControl'

export type { LmsAccess, LmsAccessInput, SessionAccess } from './lmsAccess'

export { getAccess } from './accessSnapshot'

export interface AccessContextProps {
  modules: {
    administration: boolean
    learningPaths: boolean
    monitoring: boolean
    reports: boolean
  }
  loading: boolean
  access: LmsAccess
  can: LmsAccess['can']
  canAll: LmsAccess['canAll']
  canAccessCourse: LmsAccess['canAccessCourse']
  canAccessLearningPath: LmsAccess['canAccessLearningPath']
  isAdmin: LmsAccess['isAdmin']
  isSuperadmin: LmsAccess['isSuperadmin']
  isScopedUser: LmsAccess['isScopedUser']
  isEndUser: LmsAccess['isEndUser']
}

export const AccessContext = createContext<AccessContextProps>(
  Object.create(null)
)

export const AccessProvider = ({ children }: { children: React.ReactNode }) => {
  const { loading, access } = useAccessControl()

  const {
    can,
    canAll,
    canAccessCourse,
    canAccessLearningPath,
    isAdmin,
    isSuperadmin,
    isScopedUser,
    isEndUser,
  } = access

  const canManageCourses = access.canManageCourses()
  const viewCourseDetails = can('viewCourseDetails')
  const manageLearningPaths = can('manageLearningPaths')

  return (
    <AccessContext.Provider
      value={{
        modules: {
          administration: canManageCourses || viewCourseDetails,
          learningPaths: manageLearningPaths,
          monitoring: access.canAccessMonitoringSection(),
          reports: isAdmin(),
        },
        loading,
        access,
        can,
        canAll,
        canAccessCourse,
        canAccessLearningPath,
        isAdmin,
        isSuperadmin,
        isScopedUser,
        isEndUser,
      }}
    >
      {children}
    </AccessContext.Provider>
  )
}

export const AccessConsumer = AccessContext.Consumer

export const useAccess = () => useContext(AccessContext)
